/**
 * Lumi wishlist: product handles saved on this device (localStorage via Lumi.storage).
 *
 *   Lumi.wishlist.get()          -> string[] of handles, newest first
 *   Lumi.wishlist.has(handle)    -> boolean
 *   Lumi.wishlist.toggle(handle) -> boolean (true when now saved)
 *   Lumi.wishlist.clear()
 *   Lumi.wishlist.sync(root)     -> refresh every [data-wishlist-toggle] inside root
 *
 * Emits lumi:wishlist:changed { handles } on document after every change.
 * Toggle buttons: <button data-wishlist-toggle data-handle="..." data-label-add="..."
 * data-label-remove="..." aria-pressed="false">. Buttons inserted later are synced
 * automatically (MutationObserver); calling sync(root) yourself is also fine.
 */
(function () {
  if (window.__lumiWishlistLoaded) return;
  window.__lumiWishlistLoaded = true;

  const Lumi = (window.Lumi = window.Lumi || {});
  const KEY = 'lumi:wishlist';
  const MAX = 100;
  const SELECTOR = '[data-wishlist-toggle]';

  function read() {
    const list = Lumi.storage ? Lumi.storage.get(KEY, []) : [];
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && h) : [];
  }

  function write(list) {
    if (Lumi.storage) Lumi.storage.set(KEY, list.slice(0, MAX));
    syncAll();
    if (Lumi.emit) Lumi.emit('lumi:wishlist:changed', { handles: list.slice() });
  }

  function paint(button, saved) {
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    const label = saved ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  }

  function sync(root) {
    const scope = root || document;
    const list = read();
    const buttons = [];
    if (scope.matches && scope.matches(SELECTOR)) buttons.push(scope);
    if (scope.querySelectorAll) buttons.push(...scope.querySelectorAll(SELECTOR));
    buttons.forEach((b) => paint(b, list.includes(b.dataset.handle)));
  }

  function syncAll() {
    sync(document);
  }

  Lumi.wishlist = {
    get: read,
    has: (handle) => read().includes(handle),
    toggle(handle) {
      if (!handle) return false;
      const list = read();
      const i = list.indexOf(handle);
      if (i === -1) list.unshift(handle);
      else list.splice(i, 1);
      write(list);
      return i === -1;
    },
    clear() {
      write([]);
    },
    sync,
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest(SELECTOR);
    if (!button) return;
    event.preventDefault();
    Lumi.wishlist.toggle(button.dataset.handle);
  });

  // Another tab changed the list.
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY) return;
    syncAll();
    if (Lumi.emit) Lumi.emit('lumi:wishlist:changed', { handles: read() });
  });

  // Cards inserted later (quick view, wishlist page, recommendations) pick up their state.
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if ((node.matches && node.matches(SELECTOR)) || node.querySelector(SELECTOR)) sync(node);
      }
    }
  });

  function init() {
    syncAll();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
